import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChatIcon from '@mui/icons-material/Chat';
import HandymanIcon from '@mui/icons-material/Handyman';
import SignpostIcon from '@mui/icons-material/Signpost';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import { Menu, Stack, Typography } from '@mui/material';
import { NodeVariants } from 'features/co-pilot/co-pilot.types';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactFlow } from 'reactflow';
import useBottomNodeMenuStore from 'stores/bottom-node-menu-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

interface Props {
  anchorEl: any | null;
  menuOpen: boolean;

  handleAddNode(
    nodeVariant: NodeVariants,
    nodeId?: string | null,
    position?: { x: number; y: number } | null,
    height?: number | null,
    path?: 'success' | 'failure' | null
  ): void;

  handleCloseMenu(): void;
}

const FunctionItem: React.FC<{ icon: React.ReactNode; text: string; handleClick(): void }> = ({ icon, text, handleClick }) => {
  const { classes, cx } = useStyles();

  return (
    <Stack className={classes.functionItem} justifyContent={'center'} alignItems={'center'} onClick={handleClick} gap={1}>
      <Stack className={cx(classes.disabledAddIcon, 'function-icon-disabled')} justifyContent={'center'} alignItems={'center'}>
        <AddRoundedIcon />
      </Stack>
      {icon}
      <Typography className={cx(classes.functionText, 'function-text')}>{text}</Typography>
    </Stack>
  );
};

const BottomNodeMenu: React.FC<Props> = ({ anchorEl, menuOpen, handleAddNode, handleCloseMenu }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { nodeId, position, height, path, fromEdge } = useBottomNodeMenuStore((state) => ({
    nodeId: state.targetNodeId,
    position: state.targetNodePosition,
    height: state.targetNodeHeight,
    path: state.targetNodePath,
    fromEdge: state.fromEdge,
  }));

  const { getNode, getNodes, getEdges } = useReactFlow();

  const [allowedNodeOptions, setAllowedNodeOptions] = useState<NodeVariants[]>([]);

  useEffect(() => {
    if (!menuOpen) return;

    let options: NodeVariants[] = [];
    const nodes = getNodes();
    const edges = getEdges();

    if (_.isNil(nodeId)) {
      if (_.isEmpty(nodes)) {
        options = [NodeVariants.FUNCTION_NODE, NodeVariants.CONDITIONAL_NODE];
      } else {
        options = [NodeVariants.FUNCTION_NODE, NodeVariants.WAIT_NODE, NodeVariants.CONDITIONAL_NODE, NodeVariants.UTILITY_NODE];
      }
    } else {
      const sourceNode = getNode(nodeId);

      let existingEdge;

      if (!_.isNil(path)) {
        existingEdge = edges.find((edge) => edge.source === sourceNode?.id && edge.sourceHandle === path);
      } else {
        existingEdge = edges.find((edge) => edge.source === sourceNode?.id);
      }

      const targetNode = nodes.find((node) => node.id === existingEdge?.target);

      if (fromEdge) {
        options = [NodeVariants.FUNCTION_NODE, NodeVariants.WAIT_NODE];
      } else {
        options = [NodeVariants.FUNCTION_NODE, NodeVariants.WAIT_NODE, NodeVariants.CONDITIONAL_NODE, NodeVariants.UTILITY_NODE];
      }

      if (sourceNode?.type === NodeVariants.FUNCTION_NODE || targetNode?.type === NodeVariants.FUNCTION_NODE) {
        options = options.filter((variant) => variant !== NodeVariants.FUNCTION_NODE);
      }

      if (sourceNode?.type === NodeVariants.WAIT_NODE || targetNode?.type === NodeVariants.WAIT_NODE) {
        options = options.filter((variant) => variant !== NodeVariants.WAIT_NODE);
      }

      if (sourceNode?.type === NodeVariants.UTILITY_NODE || targetNode?.type === NodeVariants.UTILITY_NODE) {
        options = options.filter((variant) => variant !== NodeVariants.UTILITY_NODE);
      }
    }

    setAllowedNodeOptions(options);
  }, [menuOpen, fromEdge, nodeId, path, getNode, getNodes, getEdges]);

  useEffect(() => {
    if (!menuOpen) {
      const resetTimeout = setTimeout(() => {
        setAllowedNodeOptions([NodeVariants.FUNCTION_NODE, NodeVariants.WAIT_NODE, NodeVariants.CONDITIONAL_NODE, NodeVariants.UTILITY_NODE]);
      }, 225);
      return () => clearTimeout(resetTimeout);
    }
    return;
  }, [menuOpen]);

  return (
    <Menu
      classes={{ list: classes.menuList, paper: classes.menuPaper }}
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={handleCloseMenu}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      slotProps={{ paper: { style: { width: 'auto' } } }}
      disableEnforceFocus={true}
      disableAutoFocus={true}
      disableAutoFocusItem={true}
      disableRestoreFocus={true}
    >
      <Stack direction={'row'} gap={2}>
        {allowedNodeOptions.includes(NodeVariants.FUNCTION_NODE) && (
          <FunctionItem
            icon={<ChatIcon className={'function-icon'} />}
            text={t('message')}
            handleClick={() => {
              handleAddNode(NodeVariants.FUNCTION_NODE, nodeId, position, height, path);
              handleCloseMenu();
            }}
          />
        )}
        {allowedNodeOptions.includes(NodeVariants.CONDITIONAL_NODE) && (
          <FunctionItem
            icon={<SignpostIcon className={'function-icon'} />}
            text={t('condition')}
            handleClick={() => {
              handleAddNode(NodeVariants.CONDITIONAL_NODE, nodeId, position, height, path);
              handleCloseMenu();
            }}
          />
        )}
        {allowedNodeOptions.includes(NodeVariants.WAIT_NODE) && (
          <FunctionItem
            icon={<WatchLaterIcon className={'function-icon'} />}
            text={t('waitingTime')}
            handleClick={() => {
              handleAddNode(NodeVariants.WAIT_NODE, nodeId, position, height, path);
              handleCloseMenu();
            }}
          />
        )}
        {allowedNodeOptions.includes(NodeVariants.UTILITY_NODE) && (
          <FunctionItem
            icon={<HandymanIcon className={'function-icon'} />}
            text={t('withdraw')}
            handleClick={() => {
              handleAddNode(NodeVariants.UTILITY_NODE, nodeId, position, height, path);
              handleCloseMenu();
            }}
          />
        )}
        {_.isEmpty(allowedNodeOptions) ? <Typography className={classes.infoText}>{t('noNodesMessage')}</Typography> : null}
      </Stack>
    </Menu>
  );
};

const useStyles = makeStyles()((theme) => ({
  menuPaper: {
    marginTop: '16px',
    borderRadius: '8px',
    boxShadow: Shadows.GREY_SHADOW_MAIN,
  },
  menuList: {
    padding: '16px',
  },
  infoText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  functionItem: {
    position: 'relative',
    cursor: 'pointer',
    width: '150px',
    borderRadius: '12px',
    border: `2px solid ${theme.palette.greys.lighter}`,
    padding: '24px 0 8px 0',
    '& .function-icon': {
      fontSize: theme.typography.pxToRem(40),
      color: theme.palette.greys.medium,
    },
    '&:hover': {
      background: theme.palette.action.hover,
      border: `2px solid ${theme.palette.primary.main}`,
      '& .function-icon': {
        color: theme.palette.primary.main,
      },
      '& .function-icon-disabled': {
        background: theme.palette.primary.main,
      },
      '& .function-text': {
        color: theme.palette.text.primary,
      },
    },
  },
  disabledAddIcon: {
    position: 'absolute',
    top: '-12px',
    padding: '2px',
    borderRadius: '50%',
    background: theme.palette.greys.lighter,
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
      color: '#FFF',
    },
  },
  functionText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
}));

export default BottomNodeMenu;
